import PropTypes from 'prop-types';
import React from 'react';
import MaskedInput from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { get } from 'ctm-util-functions';

import { Input } from '../input/input';

/**
 * Regexes for sanitizing values during onChange for various meta types
 * For example: We can't rely on just setting type=number for formatted fields as the fields will then not allow commas
 * which are applied by the masking, as soon as a comma is inserted by the masking the field clears.
 * These regexes allow us to sanitize the input but still allow the masking.
 * @type {{default: RegExp, landline: RegExp, mobile: RegExp}}
 */
const sanitizeRegexes = {
    default: /[^0-9.]+/g,
    landline: /\D/g,
    mobile: /\D/g,
    postcode: /\D/g
};

/**
 * Private function to get the appropriate mask for specific inputType
 * @param {String} inputType - one of mobile, landline, or default
 * @param {Boolean} allowDecimal - true to allow decimals in the masking, false otherwise
 * @param {Number|null} decimalLimit - number to limit the decimal length, null means no limit
 * @param {Boolean} includeThousandsSeparator - true to include commas every 3 digits
 * @returns {Array|Function} - regex array or
 * @private
 */
const _getMask = (inputType, allowDecimal, decimalLimit, includeThousandsSeparator) => {
    const masks = {
        /**
         * Default number mask, e.g. 123,456,789
         * Uses text-mask-addon: https://github.com/text-mask/text-mask/tree/master/addons/#readme
         */
        default: createNumberMask({
            prefix: '', // no prefix as we prefixed with input layout
            allowLeadingZeroes: true,
            allowDecimal,
            decimalLimit,
            includeThousandsSeparator
        }),
        /**
         * Allows landline numbers i.e. (07) 1122 3344
         */
        landline: ['(', /[0]/, /\d/, ')', ' ', /\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/],
        /**
         * Allows Mobile numbers i.e. 0444 444 444
         */
        mobile: [/[0]/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/],
        /**
         * Allows Postcode numbers i.e. 4066
         */
        postcode: [/\d/, /\d/, /\d/, /\d/]
    };

    return masks[inputType];
};

/**
 * Number field input masking for thousands values
 * @param {Object} props React props
 * @returns {FormattedInput} A Input component with extra props to renter masked input
 */
const FormattedInput = (props) => {
    const { inputType = 'default', allowDecimal = false, decimalLimit = null, includeThousandsSeparator = true, customMask } = get(props, 'schema.field', props);
    const sanitizeRegex = sanitizeRegexes[inputType];
    // Use customMask from schema if provided, otherwise use default mask logic
    const mask = customMask || _getMask(inputType, allowDecimal, decimalLimit, includeThousandsSeparator);
    const maskProps = {
        /*
             * @warn When upgrading react-text-mask,
             * ensure you test 0411 111 111 and pressing backspace to delete the whole number.
             * Also look at landline 07 3333 3333 and ensure it can be typed and back spaced.
             * Also test moving your caret to the middle, deleting a character, and entering a new one.
             * Also test on Android/iOS
             * Now the issue is temp fixed by toggling the combination of using guide and keepCharPositions
             * For currencies, we have to turn guide off
             * For phones we have to turn both on (guide is now passed through schema and have a default to false)
             * @see: https://github.com/text-mask/text-mask/issues/480
             */
        mask,
        guide: get(props, 'schema.field.guide', false),
        keepCharPositions: get(props, 'schema.field.keepCharPositions', true),
        placeholderChar: '\u2000'
    };

    return (
        <Input
            {...props}
            componentType={MaskedInput}
            componentTypeProps={maskProps}
            sanitizeRegex={sanitizeRegex}
        />
    );
};

FormattedInput.propTypes = {
    schema: PropTypes.object
};

export { FormattedInput };
